import React, { useEffect, useState } from 'react';
import { Modal, Button as BsButton, Form } from 'react-bootstrap';
import Button from 'components/base/Button';
import {
  ApiToken,
  MAX_API_TOKEN_DESCRIPTION_LENGTH,
  UpdateApiTokenRequest,
} from 'models/ApiToken';

type ApiTokenEditFormProps = {
  apiToken: ApiToken;
  onCancel: () => void;
  handleUpdate: (id: number, data: UpdateApiTokenRequest) => Promise<void>;
  onSaved: () => void;
};

const ApiTokenEditForm: React.FC<ApiTokenEditFormProps> = ({
  apiToken,
  onCancel,
  handleUpdate,
  onSaved,
}) => {
  const [description, setDescription] = useState(apiToken.description);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  useEffect(() => {
    setDescription(apiToken.description);
    setError(null);
    setFieldErrors({});
  }, [apiToken]);

  const submitUpdate = async () => {
    setSubmitting(true);
    setError(null);
    setFieldErrors({});

    const trimmedDescription = description.trim();

    if (!trimmedDescription) {
      setFieldErrors({
        description: ['Description is required.'],
      });
      setSubmitting(false);
      return;
    }

    if (trimmedDescription.length > MAX_API_TOKEN_DESCRIPTION_LENGTH) {
      setFieldErrors({
        description: [
          `Description must be at most ${MAX_API_TOKEN_DESCRIPTION_LENGTH} characters.`,
        ],
      });
      setSubmitting(false);
      return;
    }

    const payload: UpdateApiTokenRequest = {
      description: trimmedDescription,
    };

    try {
      await handleUpdate(apiToken.id, payload);
      onSaved();
    } catch (err: unknown) {
      const validationErrors = (err as Error & { validationErrors?: Record<string, string[]> })
        .validationErrors;
      if (validationErrors) {
        setFieldErrors(validationErrors);
      }
      setError(err instanceof Error ? err.message : 'Failed to update API token');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !description.trim()) return;
    void submitUpdate();
  };

  return (
    <Modal show onHide={onCancel} centered>
      <Form onSubmit={handleFormSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Update API Token Description</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <div className="alert alert-outline-danger mb-3" role="alert">
              {error}
            </div>
          )}
          <Form.Group controlId="editApiTokenDescription" className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={MAX_API_TOKEN_DESCRIPTION_LENGTH}
              isInvalid={Boolean(fieldErrors.description)}
              aria-describedby="editApiTokenDescriptionHelp"
            />
            {fieldErrors.description && (
              <Form.Control.Feedback type="invalid">
                {fieldErrors.description.join(' ')}
              </Form.Control.Feedback>
            )}
            <Form.Text id="editApiTokenDescriptionHelp" className="text-muted">
              {description.length}/{MAX_API_TOKEN_DESCRIPTION_LENGTH} characters
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <BsButton variant="secondary" type="button" onClick={onCancel} disabled={submitting}>
            Cancel
          </BsButton>
          <Button
            variant="primary"
            type="submit"
            loading={submitting}
            loadingPosition="start"
            disabled={submitting || !description.trim()}
            style={{ minWidth: '6.25rem' }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ApiTokenEditForm;
